<template>
  <div class="collection-grid-cell" @click="$emit('select', collection)">
    <div class="cell-header">
      <div class="cell-icon">
        <i class="el-icon-folder"></i>
      </div>
      <el-dropdown
        trigger="click"
        @command="handleCommand"
        @click.stop.native
      >
        <i class="el-icon-more cell-menu"></i>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="edit">Edit</el-dropdown-item>
          <el-dropdown-item command="delete" style="color: #F56C6C;">Delete</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <div class="cell-body">
      <h3 class="cell-title">{{ collection.name }}</h3>
      <p v-if="collection.description" class="cell-description">{{ collection.description }}</p>
      <div class="cell-count">
        <i class="el-icon-document"></i>
        <span>{{ collection.card_count || 0 }} cards</span>
      </div>
    </div>

    <div v-if="getMedalInfo(collection.card_count || 0)" class="cell-footer">
      <div class="medal-badge" :style="{ background: getMedalInfo(collection.card_count || 0).color }">
        <span class="medal-icon">{{ getMedalInfo(collection.card_count || 0).icon }}</span>
        <span class="medal-text">{{ getMedalInfo(collection.card_count || 0).name }}</span>
      </div>
    </div>

    <div v-if="getMedalProgress(collection.card_count || 0)" class="cell-progress">
      <div class="progress-info">
        <span class="progress-label">Next: {{ getMedalProgress(collection.card_count || 0).nextMedal }}</span>
        <span class="progress-count">{{ collection.card_count || 0 }}/{{ getMedalProgress(collection.card_count || 0).nextThreshold }}</span>
      </div>
      <el-progress
        :percentage="getMedalProgress(collection.card_count || 0).percentage"
        :stroke-width="4"
        :color="getMedalProgress(collection.card_count || 0).color"
        :show-text="false"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardCollectionGridCell',
  props: {
    collection: {
      type: Object,
      required: true
    }
  },
  methods: {
    handleCommand(command) {
      this.$emit('command', { command, collection: this.collection })
    },

    getMedalInfo(cardCount) {
      if (cardCount >= 100) {
        return { name: 'Titanium Collection', icon: '💎', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
      } else if (cardCount >= 50) {
        return { name: 'Platinum Collection', icon: '🏆', color: 'linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)' }
      } else if (cardCount >= 25) {
        return { name: 'Gold Collection', icon: '🥇', color: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' }
      } else if (cardCount >= 15) {
        return { name: 'Silver Collection', icon: '🥈', color: 'linear-gradient(135deg, #e0e0e0 0%, #adadad 100%)' }
      } else if (cardCount >= 10) {
        return { name: 'Bronze Collection', icon: '🥉', color: 'linear-gradient(135deg, #ff9a56 0%, #b86f50 100%)' }
      }
      return null
    },

    getMedalProgress(cardCount) {
      const thresholds = [
        { threshold: 10, medal: 'Bronze Collection', color: '#ff9a56' },
        { threshold: 15, medal: 'Silver Collection', color: '#c0c0c0' },
        { threshold: 25, medal: 'Gold Collection', color: '#ffd700' },
        { threshold: 50, medal: 'Platinum Collection', color: '#a8c0ff' },
        { threshold: 100, medal: 'Titanium Collection', color: '#667eea' }
      ]

      for (let i = 0; i < thresholds.length; i++) {
        if (cardCount < thresholds[i].threshold) {
          const prevThreshold = i > 0 ? thresholds[i - 1].threshold : 0
          const progress = ((cardCount - prevThreshold) / (thresholds[i].threshold - prevThreshold)) * 100
          return {
            percentage: Math.min(progress, 100),
            nextMedal: thresholds[i].medal,
            nextThreshold: thresholds[i].threshold,
            color: thresholds[i].color
          }
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.collection-grid-cell {
  background: white;
  border-radius: 12px;
  border: 2px solid #e4e7ed;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
}

.collection-grid-cell:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.cell-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cell-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.cell-menu {
  font-size: 18px;
  color: #909399;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.2s;
}

.cell-menu:hover {
  background: #f5f7fa;
  color: #606266;
}

.cell-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cell-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.cell-description {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.cell-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

.cell-count i {
  font-size: 16px;
}

.cell-footer {
  margin-top: auto;
}

.medal-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.medal-icon {
  font-size: 16px;
  display: flex;
  align-items: center;
}

.medal-text {
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 11px;
}

.cell-progress {
  margin-top: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.progress-label {
  color: #606266;
  font-weight: 500;
}

.progress-count {
  color: #909399;
  font-weight: 600;
}

:deep(.el-progress-bar__outer) {
  background-color: #e4e7ed;
  border-radius: 100px;
  height: 4px !important;
}

:deep(.el-progress-bar__inner) {
  border-radius: 100px;
  transition: all 0.3s ease;
}
</style>
